import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Trophy, Flame, TrendingUp, TrendingDown, Minus,
  Crown, Medal, Target, ChevronUp, ChevronDown, Star,
} from 'lucide-react';

// ─── Mock Data ──────────────────────────────────────────────────────

interface LeaderboardServer {
  id: string;
  name: string;
  restaurant: string;
  city: string;
  score: number;
  streak: number;
  upsellRate: number;
  rankChange: number;
  avatar: string; // initials
  isYou?: boolean;
  tier: 'diamond' | 'gold' | 'silver' | 'bronze';
}

const SERVERS: LeaderboardServer[] = [
  { id: '1', name: 'Emma Martinez', restaurant: 'Le Bernardin', city: 'NYC', score: 2847, streak: 14, upsellRate: 93, rankChange: 1, avatar: 'EM', tier: 'diamond' },
  { id: '2', name: 'Sarah Chen', restaurant: 'SAJJ Mediterranean', city: 'SF', score: 2731, streak: 9, upsellRate: 87, rankChange: 2, avatar: 'SC', tier: 'diamond' },
  { id: '3', name: 'Jake Bennett', restaurant: 'Alinea', city: 'CHI', score: 2698, streak: 11, upsellRate: 90, rankChange: -1, avatar: 'JB', tier: 'diamond' },
  { id: '4', name: 'Marcus Williams', restaurant: 'Gen Korean BBQ', city: 'LA', score: 2654, streak: 7, upsellRate: 84, rankChange: 0, avatar: 'MW', tier: 'gold' },
  { id: '5', name: 'Hannah Wilson', restaurant: 'The Capital Grille', city: 'PDX', score: 2601, streak: 5, upsellRate: 89, rankChange: 3, avatar: 'HW', tier: 'gold' },
  { id: '6', name: 'David Johnson', restaurant: 'Shake Shack', city: 'NYC', score: 2578, streak: 4, upsellRate: 83, rankChange: -2, avatar: 'DJ', tier: 'gold' },
  { id: 'you', name: 'You', restaurant: 'Gen Korean BBQ', city: 'Fremont', score: 2534, streak: 6, upsellRate: 78, rankChange: 4, avatar: 'YO', isYou: true, tier: 'gold' },
  { id: '8', name: 'Kayla Brown', restaurant: 'Chick-fil-A', city: 'ATX', score: 2498, streak: 3, upsellRate: 82, rankChange: -1, avatar: 'KB', tier: 'silver' },
  { id: '9', name: 'Sofia Rodriguez', restaurant: 'Husk', city: 'NSH', score: 2467, streak: 2, upsellRate: 81, rankChange: 1, avatar: 'SR', tier: 'silver' },
  { id: '10', name: 'Chris Miller', restaurant: 'Canlis', city: 'SEA', score: 2445, streak: 8, upsellRate: 88, rankChange: -3, avatar: 'CM', tier: 'silver' },
  { id: '11', name: 'Alex Park', restaurant: "Canter's Deli", city: 'LA', score: 2412, streak: 1, upsellRate: 81, rankChange: 2, avatar: 'AP', tier: 'silver' },
  { id: '12', name: 'Ryan Nguyen', restaurant: 'Pappadeaux', city: 'DFW', score: 2389, streak: 0, upsellRate: 78, rankChange: 0, avatar: 'RN', tier: 'bronze' },
];

const tierConfig = {
  diamond: { label: 'Diamond', color: 'text-cyan-500', bg: 'bg-cyan-50', border: 'border-cyan-200', ring: 'ring-cyan-400' },
  gold: { label: 'Gold', color: 'text-amber-500', bg: 'bg-amber-50', border: 'border-amber-200', ring: 'ring-amber-400' },
  silver: { label: 'Silver', color: 'text-gray-400', bg: 'bg-gray-50', border: 'border-gray-300', ring: 'ring-gray-400' },
  bronze: { label: 'Bronze', color: 'text-orange-600', bg: 'bg-orange-50', border: 'border-orange-200', ring: 'ring-orange-400' },
};

// ─── Component ──────────────────────────────────────────────────────

const ArenaLeaderboard: React.FC = () => {
  const navigate = useNavigate();
  const [selectedView, setSelectedView] = useState<'all' | 'restaurant' | 'city'>('all');
  const [animateIn, setAnimateIn] = useState(false);

  useEffect(() => {
    setAnimateIn(true);
  }, []);

  const yourRank = SERVERS.findIndex(s => s.isYou) + 1;
  const totalServers = 847; // pretend total
  const yourPercentile = Math.round((1 - yourRank / totalServers) * 100);

  return (
    <div className="min-h-screen bg-gray-950">
      {/* Header */}
      <div className="bg-gradient-to-r from-amber-600 via-orange-600 to-red-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <button onClick={() => navigate('/restaurant/mockups')} className="flex items-center gap-1.5 text-amber-100 hover:text-white text-sm mb-4 transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to Hub
          </button>
          <div className="flex items-center gap-3">
            <Trophy className="w-8 h-8 text-amber-200" />
            <div>
              <h1 className="text-2xl font-bold text-white">The Arena</h1>
              <p className="text-amber-100 text-sm">This week's standings across all locations</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {/* Your position hero */}
        <div className="bg-gray-900 rounded-xl border border-gray-800 p-6">
          <div className="flex flex-col sm:flex-row items-center gap-6">
            <div className="relative">
              <div className={`w-20 h-20 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-2xl font-bold text-white ring-4 ${tierConfig.gold.ring}`}>
                #{yourRank}
              </div>
              <div className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-emerald-500 flex items-center justify-center">
                <ChevronUp className="w-4 h-4 text-white" />
              </div>
            </div>
            <div className="text-center sm:text-left flex-1">
              <div className="flex items-center gap-2 justify-center sm:justify-start mb-1">
                <h2 className="text-xl font-bold text-white">You're #{yourRank} this week</h2>
                <span className="text-emerald-400 text-sm font-medium flex items-center gap-0.5">
                  <TrendingUp className="w-3.5 h-3.5" /> +4 spots
                </span>
              </div>
              <p className="text-gray-400 text-sm mb-3">
                Top <span className="text-amber-400 font-bold">{yourPercentile}%</span> of {totalServers} servers nationwide
              </p>
              <div className="flex items-center gap-4 justify-center sm:justify-start">
                <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${tierConfig.gold.bg} ${tierConfig.gold.color} border ${tierConfig.gold.border}`}>
                  <Star className="w-3 h-3" /> Gold Tier
                </div>
                <div className="flex items-center gap-1 text-xs text-gray-500">
                  <Flame className="w-3.5 h-3.5 text-orange-400" />
                  <span className="text-orange-400 font-medium">6-day streak</span>
                </div>
                <div className="text-xs text-gray-500">
                  166 pts to <span className="text-cyan-400">Diamond</span>
                </div>
              </div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-white">2,534</div>
              <div className="text-xs text-gray-500 uppercase tracking-wider">points</div>
            </div>
          </div>
        </div>

        {/* Percentile visualization */}
        <div className="bg-gray-900 rounded-xl border border-gray-800 p-5">
          <h3 className="text-sm font-semibold text-white mb-3">Your Percentile</h3>
          <div className="relative h-8 bg-gray-800 rounded-full overflow-hidden">
            <div className="absolute inset-0 flex">
              <div className="h-full bg-orange-900/50" style={{ width: '25%' }} />
              <div className="h-full bg-gray-700/50" style={{ width: '25%' }} />
              <div className="h-full bg-amber-900/50" style={{ width: '25%' }} />
              <div className="h-full bg-cyan-900/50" style={{ width: '25%' }} />
            </div>
            <div
              className="absolute top-0 bottom-0 w-1 bg-white rounded-full shadow-lg shadow-white/30"
              style={{ left: `${yourPercentile}%`, transition: 'left 1s ease-out' }}
            />
            <div
              className="absolute -top-6 text-xs font-bold text-white whitespace-nowrap"
              style={{ left: `${yourPercentile}%`, transform: 'translateX(-50%)' }}
            >
              You: {yourPercentile}th
            </div>
          </div>
          <div className="flex justify-between text-[10px] text-gray-600 mt-1 px-1">
            <span>Bronze</span>
            <span>Silver</span>
            <span>Gold</span>
            <span>Diamond</span>
          </div>
        </div>

        {/* View toggle */}
        <div className="flex items-center gap-1 bg-gray-900 rounded-lg p-1 border border-gray-800 w-fit">
          {[
            { key: 'all' as const, label: 'All Servers' },
            { key: 'restaurant' as const, label: 'My Restaurant' },
            { key: 'city' as const, label: 'My City' },
          ].map((v) => (
            <button
              key={v.key}
              onClick={() => setSelectedView(v.key)}
              className={`px-4 py-1.5 text-xs font-medium rounded-md transition-colors ${
                selectedView === v.key ? 'bg-gray-800 text-white' : 'text-gray-500 hover:text-gray-300'
              }`}
            >
              {v.label}
            </button>
          ))}
        </div>

        {/* Leaderboard table */}
        <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
          <div className="divide-y divide-gray-800">
            {SERVERS.map((server, idx) => {
              const rank = idx + 1;
              const tc = tierConfig[server.tier];
              return (
                <div
                  key={server.id}
                  className={`flex items-center gap-4 px-5 py-3.5 transition-all duration-500 ${
                    server.isYou
                      ? 'bg-amber-500/10 border-l-2 border-l-amber-400'
                      : 'hover:bg-gray-800/50 border-l-2 border-l-transparent'
                  }`}
                  style={{
                    opacity: animateIn ? 1 : 0,
                    transform: animateIn ? 'translateX(0)' : 'translateX(-20px)',
                    transitionDelay: `${idx * 50}ms`,
                  }}
                >
                  {/* Rank */}
                  <div className="w-8 text-center flex-shrink-0">
                    {rank === 1 ? (
                      <Crown className="w-5 h-5 text-amber-400 mx-auto" />
                    ) : rank <= 3 ? (
                      <Medal className={`w-5 h-5 mx-auto ${rank === 2 ? 'text-gray-300' : 'text-orange-400'}`} />
                    ) : (
                      <span className="text-sm font-medium text-gray-500">{rank}</span>
                    )}
                  </div>

                  {/* Avatar */}
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                    server.isYou
                      ? 'bg-gradient-to-br from-amber-400 to-orange-500 text-white'
                      : 'bg-gray-800 text-gray-400'
                  }`}>
                    {server.avatar}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`text-sm font-medium truncate ${server.isYou ? 'text-amber-300' : 'text-white'}`}>
                        {server.name}
                      </span>
                      {server.isYou && (
                        <span className="text-[10px] font-bold text-amber-400 bg-amber-400/20 px-1.5 py-0.5 rounded uppercase">You</span>
                      )}
                      {server.streak >= 7 && (
                        <span className="flex items-center gap-0.5 text-[10px] text-orange-400">
                          <Flame className="w-3 h-3" />{server.streak}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 truncate">{server.restaurant} · {server.city}</div>
                  </div>

                  {/* Upsell rate */}
                  <div className="hidden sm:block text-right flex-shrink-0 w-16">
                    <div className="text-xs text-gray-500">Upsell</div>
                    <div className="text-sm font-medium text-gray-300">{server.upsellRate}%</div>
                  </div>

                  {/* Score */}
                  <div className="text-right flex-shrink-0 w-16">
                    <div className="text-sm font-bold text-white">{server.score.toLocaleString()}</div>
                    <div className={`text-[10px] ${tc.color}`}>{tc.label}</div>
                  </div>

                  {/* Rank change */}
                  <div className="w-10 text-right flex-shrink-0">
                    {server.rankChange > 0 ? (
                      <span className="inline-flex items-center text-xs text-emerald-400">
                        <ChevronUp className="w-3 h-3" />{server.rankChange}
                      </span>
                    ) : server.rankChange < 0 ? (
                      <span className="inline-flex items-center text-xs text-red-400">
                        <ChevronDown className="w-3 h-3" />{Math.abs(server.rankChange)}
                      </span>
                    ) : (
                      <Minus className="w-3 h-3 text-gray-600 inline" />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Bottom motivation */}
        <div className="text-center py-4">
          <p className="text-xs text-gray-600">Updated every shift. Keep pushing — Diamond tier unlocks 2x reward points.</p>
        </div>
      </div>
    </div>
  );
};

export default ArenaLeaderboard;
